import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import Select from 'react-select';
import BillList from './BillList';
import PaymentModal from './PaymentModal';
import LoadingDots from '../../shared/LoadingDots';
import {
  fetchBills,
  previewBill,
  clearPreview,
  selectBillPreview,
  selectBills,
  selectBillsLoading,
  selectBillsError,
  selectShowPaymentModal,
  billUpdated
} from '../../../ReduxSlices/Bills/billSlice';
import { baseSelectStyles } from '../../shared/selectStyles';

const BillingSection = () => {
const dispatch = useDispatch();

const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    ref={ref}
    value={value ? format(new Date(value), "do MMM, yyyy") : ""}
    onClick={onClick}
    readOnly
    placeholder={placeholder} 
    className="date-picker-input"
    style={{
      marginLeft: '10px',
      backgroundColor: '#E9F7EE',
      border: '1px solid #A5D6A7',
      borderRadius: '8px',
      padding: '0 4px',      
      height: '30px',           
      fontSize: '13px',        
     }}
  />
));

const filterOptions = [
  { value: 'today', label: 'Today' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Specific Day' },
  { value: 'range', label: 'Date Range' },
];

 //Select data from redux store
const bills = useSelector(selectBills);
const loaded = useSelector((state) => state.bills.loaded);
const loading = useSelector(selectBillsLoading);
const paymentPreview = useSelector(selectBillPreview);
const showPaymentModal = useSelector(selectShowPaymentModal);
const error = useSelector(selectBillsError);
const [previewLoading, setPreviewLoading] = useState(false);

  const [filter, setFilter] = useState('today');
  const [customDate, setCustomDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {

    dispatch(fetchBills());
   
  }, [dispatch]);

  const handleInitiatePayment = async (patientId, category, serviceId, patientQueueId = null) => {
      try {
    setPreviewLoading(true);
    await dispatch(previewBill({ patientId, category, serviceId, patientQueueId: patientQueueId || null, })).unwrap(); 
  } catch (err) {
    console.error('Failed to preview bill:', err);
  } finally {
    setPreviewLoading(false);
  } 
  };

const filterBills = (bills, period) => {
  const now = new Date();

  return bills.filter((bill) => {
    const billDate = new Date(bill.created_at);

    switch (period) {
      case 'today':
        return billDate.toDateString() === now.toDateString();
      case 'month':
        return (
          billDate.getMonth() === now.getMonth() &&
          billDate.getFullYear() === now.getFullYear()
        );
      case 'year':
        return billDate.getFullYear() === now.getFullYear();
      case 'custom':
        if (!customDate) return true;
        return billDate.toDateString() === new Date(customDate).toDateString();
      case 'range':
        if (!startDate || !endDate) return false;
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (start > end) return false;
        end.setHours(23, 59, 59, 999);
        return billDate >= start && billDate <= end;
        default:
        return true;
    }
  });
};

const filteredBills = useMemo(() => {
  if (!bills || bills.length === 0) return [];  // fallback for no bills
  return filterBills(bills, filter);
}, [bills, filter, customDate, startDate, endDate]);

// console.log('filtered bills:', filteredBills)


{error && <p style={{ color: 'red' }}>Error: {error}</p>}

  return (
    <div>
      <h2>Billing Records</h2>

    <div className="ranges">
      <label>Filter: </label>
     <Select
          value={filterOptions.find(option => option.value === filter) || null}
          onChange={(selectedOption) => {
            const value = selectedOption.value;
            setFilter(value);

            if (value !== 'custom') setCustomDate('');
            if (value !== 'range') {
              setStartDate('');
              setEndDate('');
            }
          }}
          options={filterOptions}
          placeholder="Select filter"
          styles={baseSelectStyles}

        />

    
        {filter === 'custom' && (
          <DatePicker
            selected={customDate ? new Date(customDate) : null}
            onChange={(date) =>
              setCustomDate(date ? date.toISOString().slice(0, 10) : '')
            }
            customInput={<CustomInput placeholder="Select a date" />}
            placeholderText="Select a date"
          />
        )}
    
        {filter === 'range' && (
      <>
        <DatePicker
          selected={startDate ? new Date(startDate) : null}
          onChange={(date) =>
            setStartDate(date ? date.toISOString().slice(0, 10) : '')
          }
          selectsStart
          startDate={startDate ? new Date(startDate) : null}
          endDate={endDate ? new Date(endDate) : null}
          customInput={<CustomInput placeholder="Start date" />}
          placeholderText="Start date"
        />
        <DatePicker
          selected={endDate ? new Date(endDate) : null}
          onChange={(date) =>
            setEndDate(date ? date.toISOString().slice(0, 10) : '')
          }
          selectsEnd
          startDate={startDate ? new Date(startDate) : null}
          endDate={endDate ? new Date(endDate) : null}
          minDate={startDate ? new Date(startDate) : null}
          customInput={<CustomInput placeholder="End date" />}
          placeholderText="End date"
          style={{ marginLeft: '10px' }}
        />
      </>
    )}
 
    </div>

      {loading ? (
        <LoadingDots height={10} width={30} color="#38AD3E" />
      ) : (
        <BillList
          bills={filteredBills}
          onPay={(patientId, category, serviceId, patientQueueId) => 
            handleInitiatePayment(patientId, category, serviceId, patientQueueId)}
           />
      )}

     {previewLoading ? (
        <div className='overlay-loading'>
          <LoadingDots height={20} width={50} color="#38AD3E" />
        </div>
        ) : showPaymentModal && paymentPreview ? (
          <PaymentModal
            previewData={paymentPreview}
            onClose={() => dispatch(clearPreview())}
            onPaymentSuccess={() => {
              dispatch(billUpdated({ ...paymentPreview.bill, status: 'paid' }));
              dispatch(clearPreview());
            }}
          />
        ) : null}

    </div>
  );
};

export default BillingSection;
